import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../LanguageContext';
import { fetchGoogleAdsCampaigns, fetchGoogleAdsKeywords, fetchGoogleAdsAdGroups } from '../api';
import LoadingSkeleton from '../components/LoadingSkeleton';
import EmptyState from '../components/EmptyState';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const BLUE = '#4285F4';
const GREEN = '#34A853';
const RED = '#EA4335';
const YELLOW = '#FBBC04';

const tooltipStyle = {
  backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)',
  borderRadius: 10, color: 'var(--text-primary)', boxShadow: 'var(--card-hover-shadow)',
};

const fmtK = n => { if (n == null) return '\u2014'; if (n >= 1e6) return (n/1e6).toFixed(1)+'M'; if (n >= 1e3) return (n/1e3).toFixed(1)+'K'; return Number(n).toLocaleString(); };
const fmtSAR = n => n != null ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' SAR' : '\u2014';

function getLocalDate(daysAgo) {
  const d = new Date(); d.setDate(d.getDate() - daysAgo);
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Riyadh' });
}

function QualityDot({ score }) {
  if (score == null) return <span className="text-secondary">\u2014</span>;
  const color = score >= 7 ? GREEN : score >= 5 ? YELLOW : RED;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
      <span style={{ fontWeight: 700, color }}>{score}/10</span>
    </span>
  );
}

function GoogleAdsLive() {
  const { t } = useLanguage();
  const [preset, setPreset] = useState('yesterday');
  const [section, setSection] = useState('campaigns');
  const [kwSearch, setKwSearch] = useState('');

  const [campData, setCampData] = useState(null);
  const [kwData, setKwData] = useState(null);
  const [agData, setAgData] = useState(null);
  const [loading, setLoading] = useState({ campaigns: true, keywords: false, adgroups: false });
  const [error, setError] = useState(null);

  const getDates = useCallback(() => {
    switch (preset) {
      case 'today': return { from: getLocalDate(0), to: getLocalDate(0) };
      case 'yesterday': return { from: getLocalDate(1), to: getLocalDate(1) };
      case '3d': return { from: getLocalDate(3), to: getLocalDate(0) };
      case '7d': return { from: getLocalDate(7), to: getLocalDate(0) };
      case '90d': return { from: getLocalDate(90), to: getLocalDate(0) };
      default: return { from: getLocalDate(30), to: getLocalDate(0) };
    }
  }, [preset]);

  const loadAll = useCallback(async (sec) => {
    const { from, to } = getDates();
    if (sec === 'campaigns' || !sec) {
      setLoading(p => ({ ...p, campaigns: true })); setError(null);
      const res = await fetchGoogleAdsCampaigns(from, to);
      if (res.error) { setError(res.error); setCampData(null); } else setCampData(res);
      setLoading(p => ({ ...p, campaigns: false }));
    }
    if (sec === 'keywords') {
      setLoading(p => ({ ...p, keywords: true })); setError(null);
      const res = await fetchGoogleAdsKeywords(from, to);
      if (res.error) { setError(res.error); setKwData({ keywords: [] }); } else setKwData(res);
      setLoading(p => ({ ...p, keywords: false }));
    }
    if (sec === 'adgroups') {
      setLoading(p => ({ ...p, adgroups: true })); setError(null);
      const res = await fetchGoogleAdsAdGroups(from, to);
      if (res.error) { setError(res.error); setAgData({ adGroups: [] }); } else setAgData(res);
      setLoading(p => ({ ...p, adgroups: false }));
    }
  }, [getDates]);

  useEffect(() => { setCampData(null); setKwData(null); setAgData(null); loadAll('campaigns'); if (section !== 'campaigns') loadAll(section); // eslint-disable-next-line
  }, [preset]);

  useEffect(() => {
    if (section === 'keywords' && !kwData && !loading.keywords) loadAll('keywords');
    if (section === 'adgroups' && !agData && !loading.adgroups) loadAll('adgroups');
    // eslint-disable-next-line
  }, [section]);

  const handleRefresh = () => { setCampData(null); setKwData(null); setAgData(null); loadAll('campaigns'); loadAll(section); };

  const tot = campData?.totals || {};
  const campList = campData?.campaigns || [];
  const kwListRaw = kwData?.keywords || [];
  const agList = agData?.adGroups || [];

  // Keyword search filter
  const kwList = useMemo(() => {
    if (!kwSearch.trim()) return kwListRaw;
    const q = kwSearch.toLowerCase();
    return kwListRaw.filter(kw => kw.keyword.toLowerCase().includes(q) || kw.campaign.toLowerCase().includes(q) || kw.adGroup.toLowerCase().includes(q));
  }, [kwListRaw, kwSearch]);

  // Campaign spend pie
  const campPie = campList.filter(c => c.spend > 0).map((c, i) => ({
    name: c.name.length > 20 ? c.name.slice(0, 20) + '...' : c.name,
    value: Math.round(c.spend * 100) / 100,
    color: [BLUE, GREEN, YELLOW, RED, '#8b5cf6', '#06b6d4'][i % 6],
  }));

  // Keyword stats summary
  const kwStats = useMemo(() => {
    if (!kwListRaw.length) return null;
    const withQS = kwListRaw.filter(k => k.qualityScore != null);
    return {
      total: kwListRaw.length,
      avgQS: withQS.length > 0 ? (withQS.reduce((s, k) => s + k.qualityScore, 0) / withQS.length) : null,
      topSpender: kwListRaw[0]?.keyword || '',
      totalSpend: kwListRaw.reduce((s, k) => s + k.spend, 0),
    };
  }, [kwListRaw]);

  const presets = [
    { key: 'today', label: t('execToday') || 'Today' },
    { key: 'yesterday', label: t('execYesterday') },
    { key: '3d', label: t('execLast3') },
    { key: '7d', label: t('execLast7') },
    { key: '30d', label: t('execLast30') },
    { key: '90d', label: t('execLastQuarter') },
  ];

  const sections = [
    { key: 'campaigns', label: t('navCampaigns') || 'Campaigns', count: campList.length },
    { key: 'keywords', label: t('gadsKeywords') || 'Keywords', count: kwListRaw.length },
    { key: 'adgroups', label: t('gadsAdGroups') || 'Ad Groups', count: agList.length },
  ];

  return (
    <div>
      {/* Filters */}
      <div className="filter-bar">
        <div className="tabs">
          {presets.map(p => (
            <button key={p.key} className={`tab ${preset === p.key ? 'active' : ''}`} onClick={() => setPreset(p.key)}>{p.label}</button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm" onClick={handleRefresh} disabled={loading.campaigns}>
            {loading.campaigns ? t('loading') : t('refresh')}
          </button>
        </div>
      </div>

      {error && (
        <div className="warning-banner mb-6">
          Google Ads: {error.slice(0, 200)}
          {error.includes('Authorize') && <span> — <a href="/auth/google" style={{ color: 'var(--accent)', fontWeight: 600 }}>Connect</a></span>}
        </div>
      )}

      {/* KPIs */}
      {loading.campaigns ? (
        <div className="grid grid-4 mb-6">{[1,2,3,4].map(i => <div key={i} className="kpi-card"><LoadingSkeleton width="60%" height="14px" /><LoadingSkeleton width="80%" height="32px" style={{ marginTop: 8 }} /></div>)}</div>
      ) : campList.length > 0 && (
        <>
          <div className="grid grid-4 mb-4">
            <div className="kpi-card"><span className="kpi-title">{t('spend')}</span><span className="kpi-value" style={{ color: BLUE }}>{fmtSAR(tot.spend)}</span></div>
            <div className="kpi-card"><span className="kpi-title">{t('impressions')}</span><span className="kpi-value">{fmtK(tot.impressions)}</span></div>
            <div className="kpi-card"><span className="kpi-title">{t('clicks')}</span><span className="kpi-value">{fmtK(tot.clicks)}</span></div>
            <div className="kpi-card"><span className="kpi-title">{t('conversions')}</span><span className="kpi-value" style={{ color: GREEN }}>{fmtK(tot.conversions)}</span></div>
          </div>
          <div className="grid grid-4 mb-6">
            <div className="kpi-card"><span className="kpi-title">{t('ctr')}</span><span className="kpi-value" style={{ color: GREEN }}>{tot.ctr?.toFixed(2)}%</span></div>
            <div className="kpi-card"><span className="kpi-title">{t('cpc')}</span><span className="kpi-value">{fmtSAR(tot.cpc)}</span></div>
            <div className="kpi-card"><span className="kpi-title">{t('cpm')}</span><span className="kpi-value">{fmtSAR(tot.cpm)}</span></div>
            <div className="kpi-card"><span className="kpi-title">{t('channelConvRate')}</span><span className="kpi-value" style={{ color: GREEN }}>{tot.convRate?.toFixed(2)}%</span></div>
          </div>
        </>
      )}

      {/* Section tabs with counts */}
      <div className="channel-subtabs" style={{ marginBottom: 20 }}>
        {sections.map(s => (
          <button key={s.key} className={`channel-subtab ${section === s.key ? 'active' : ''}`} onClick={() => setSection(s.key)}>
            {s.label} {s.count > 0 && <span style={{ opacity: 0.6, fontSize: '0.75rem', marginLeft: 4 }}>({s.count})</span>}
          </button>
        ))}
      </div>

      {/* ── CAMPAIGNS ── */}
      {section === 'campaigns' && (
        <>
          {campList.length > 0 && (
            <div className="grid grid-2 mb-6">
              <div className="card">
                <div className="card-header"><h2>{t('channelClicksByCampaign')}</h2></div>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={campList.slice(0, 8)} layout="vertical" margin={{ left: 10, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis type="number" stroke="var(--text-secondary)" tick={{ fill:'var(--text-secondary)', fontSize:11 }} />
                      <YAxis type="category" dataKey="name" width={160} stroke="var(--text-secondary)" tick={{ fill:'var(--text-secondary)', fontSize:10 }} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend />
                      <Bar dataKey="clicks" fill={BLUE} radius={[0,6,6,0]} name={t('clicks')} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="card">
                <div className="card-header"><h2>{t('execSpendDistribution')}</h2></div>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={campPie} cx="50%" cy="50%" outerRadius={100} innerRadius={50} dataKey="value" nameKey="name" paddingAngle={3}
                        label={({name, percent}) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={{ stroke:'var(--text-secondary)', strokeWidth:1 }}>
                        {campPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} formatter={v => fmtSAR(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
          <div className="card">
            <div className="card-header"><h2>{t('channelCampaignPerf')}</h2></div>
            {loading.campaigns ? <LoadingSkeleton width="100%" height="200px" /> : campList.length === 0 ? (
              <EmptyState icon="&#128270;" title={t('execNoData')} />
            ) : (
              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead><tr>
                    <th>{t('campaign')}</th><th>{t('channelType')}</th><th>{t('status')}</th>
                    <th>{t('impressions')}</th><th>{t('clicks')}</th><th>{t('spend')}</th>
                    <th>{t('ctr')}</th><th>{t('cpc')}</th><th>{t('cpm')}</th>
                    <th>{t('conversions')}</th><th>{t('channelConvRate')}</th>
                  </tr></thead>
                  <tbody>
                    {campList.map((c, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{c.name}</td>
                        <td><span className="badge" style={{ background:'rgba(66,133,244,0.15)', color: BLUE }}>{c.type}</span></td>
                        <td><span className={`badge badge-${c.status==='Active'?'active':'paused'}`}>{c.status==='Active'?t('active'):t('paused')}</span></td>
                        <td>{fmtK(c.impressions)}</td><td style={{ fontWeight: 600 }}>{fmtK(c.clicks)}</td>
                        <td style={{ color: BLUE, fontWeight: 700 }}>{fmtSAR(c.spend)}</td>
                        <td style={{ color: GREEN }}>{c.ctr?.toFixed(2)}%</td>
                        <td>{fmtSAR(c.cpc)}</td><td>{fmtSAR(c.cpm)}</td>
                        <td style={{ fontWeight: 600 }}>{c.conversions?.toFixed(0)}</td>
                        <td>{c.convRate?.toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── KEYWORDS ── */}
      {section === 'keywords' && (
        <>
          {kwData?.note && (
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {kwData.note}
            </div>
          )}
          {/* Keyword stats bar */}
          {kwStats && (
            <div className="crm-stats-bar mb-6">
              <div className="crm-stat-item">
                <span className="crm-stat-value">{kwStats.total}</span>
                <span className="crm-stat-label">{t('gadsKeywords')}</span>
              </div>
              <div className="crm-stat-item">
                <span className="crm-stat-value" style={{ color: BLUE }}>{fmtSAR(kwStats.totalSpend)}</span>
                <span className="crm-stat-label">{t('spend')}</span>
              </div>
              <div className="crm-stat-item">
                <span className="crm-stat-value">{kwStats.avgQS != null ? kwStats.avgQS.toFixed(1) : '\u2014'}</span>
                <span className="crm-stat-label">{t('channelQualityScore')}</span>
              </div>
              <div className="crm-stat-item">
                <span className="crm-stat-value" style={{ fontSize: '0.9rem' }}>{kwStats.topSpender}</span>
                <span className="crm-stat-label">Top Spender</span>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
              <h2>{t('gadsKeywords')}</h2>
              <input value={kwSearch} onChange={e => setKwSearch(e.target.value)}
                placeholder={t('gadsKeyword') + '...'}
                style={{ width: 'auto', maxWidth: 250, padding: '7px 14px', fontSize: '0.82rem' }} />
            </div>
            {loading.keywords ? <LoadingSkeleton width="100%" height="200px" /> : kwList.length === 0 ? (
              <EmptyState icon="&#128273;" title={kwSearch ? 'No matching keywords' : t('execNoData')} />
            ) : (
              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead><tr>
                    <th>{t('gadsKeyword')}</th>
                    <th>{t('gadsMatchType')}</th>
                    <th>{t('campaign')}</th>
                    <th>{t('gadsAdGroup')}</th>
                    <th>{t('impressions')}</th>
                    <th>{t('clicks')}</th>
                    <th>{t('spend')}</th>
                    <th>{t('ctr')}</th>
                    <th>{t('cpc')}</th>
                    <th>{t('conversions')}</th>
                    <th>{t('channelQualityScore')}</th>
                  </tr></thead>
                  <tbody>
                    {kwList.map((kw, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600, direction: 'rtl', textAlign: 'right' }}>{kw.keyword}</td>
                        <td><span className="badge" style={{
                          background: kw.matchType === 'Exact' ? 'rgba(52,168,83,0.15)' : kw.matchType === 'Phrase' ? 'rgba(66,133,244,0.15)' : 'rgba(251,188,4,0.15)',
                          color: kw.matchType === 'Exact' ? GREEN : kw.matchType === 'Phrase' ? BLUE : YELLOW,
                        }}>{kw.matchType}</span></td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{kw.campaign}</td>
                        <td style={{ fontSize: '0.8rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{kw.adGroup}</td>
                        <td>{fmtK(kw.impressions)}</td>
                        <td style={{ fontWeight: 600 }}>{fmtK(kw.clicks)}</td>
                        <td style={{ color: BLUE, fontWeight: 700 }}>{fmtSAR(kw.spend)}</td>
                        <td style={{ color: GREEN }}>{kw.ctr?.toFixed(2)}%</td>
                        <td>{fmtSAR(kw.cpc)}</td>
                        <td>{kw.conversions?.toFixed(0) || '0'}</td>
                        <td><QualityDot score={kw.qualityScore} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── AD GROUPS ── */}
      {section === 'adgroups' && (
        <div className="card">
          <div className="card-header">
            <h2>{t('gadsAdGroups')}</h2>
            {agList.length > 0 && <span className="text-secondary" style={{ fontSize: '0.78rem' }}>{agList.length} ad groups</span>}
          </div>
          {loading.adgroups ? <LoadingSkeleton width="100%" height="200px" /> : agList.length === 0 ? (
            <EmptyState icon="&#128194;" title={t('execNoData')} />
          ) : (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead><tr>
                  <th>{t('gadsAdGroup')}</th>
                  <th>{t('campaign')}</th>
                  <th>{t('status')}</th>
                  <th>{t('impressions')}</th>
                  <th>{t('clicks')}</th>
                  <th>{t('spend')}</th>
                  <th>CTR</th>
                  <th>{t('cpc')}</th>
                  <th>{t('conversions')}</th>
                  <th>{t('channelConvRate')}</th>
                </tr></thead>
                <tbody>
                  {agList.map((ag, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{ag.name}</td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{ag.campaignName}</td>
                      <td><span className={`badge badge-${ag.status==='Active'?'active':'paused'}`}>{ag.status==='Active'?t('active'):t('paused')}</span></td>
                      <td>{fmtK(ag.impressions)}</td>
                      <td style={{ fontWeight: 600 }}>{fmtK(ag.clicks)}</td>
                      <td style={{ color: BLUE, fontWeight: 700 }}>{fmtSAR(ag.spend)}</td>
                      <td style={{ color: GREEN }}>{ag.ctr?.toFixed(2)}%</td>
                      <td>{fmtSAR(ag.cpc)}</td>
                      <td>{ag.conversions?.toFixed(0) || '0'}</td>
                      <td>{ag.convRate?.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default GoogleAdsLive;
